/**
 * §A.1 · El club pasa a otro correo.
 *
 * Para cuando el alta se hizo con el correo equivocado. El caso que lo trajo: un
 * lead convertido con el correo con el que su dueño ya entraba al back office, así
 * que su invitación le abría el back office y no su club. 12-10-2026.
 *
 * `PUT /admin/academies/{id}/owner` invita al correo nuevo como dueño y le quita
 * el club al anterior, haya entrado o no. Por eso se confirma diciendo quién lo
 * pierde. Lleva `Idempotency-Key` nueva por envío: manda un correo.
 *
 * Solo se pinta en un club **con** dueño: el de una ficha del censo llega por la
 * reclamación, y la API responde `409 academy_has_no_owner`. Y el formulario avisa
 * de lo que causó el caso: una cuenta del back office abre siempre el back office.
 */
import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/shared/api/client';
import { aProblema, ErrorDeApi, type Problema } from '@/shared/api/errores';
import { Error as PanelError } from '@/shared/ui/Estados';

export function CambioDeDueno({
  clubId,
  actual,
}: {
  clubId: string;
  /** El correo del dueño de ahora, que es quien pierde el club. */
  actual: string;
}) {
  const cache = useQueryClient();
  const [abierto, setAbierto] = useState(false);
  const [correo, setCorreo] = useState('');
  const [nombre, setNombre] = useState('');
  const [enviado, setEnviado] = useState<string | null>(null);
  const [problema, setProblema] = useState<Problema | null>(null);

  const cambiar = useMutation({
    mutationFn: async () => {
      const r = await api.v1.admin
        .academies({ id: clubId })
        .owner.put(
          { email: correo.trim(), first_name: nombre.trim() || null },
          { headers: { 'idempotency-key': crypto.randomUUID() } }
        );
      if (r.error) throw new ErrorDeApi(aProblema(r.error));
      return r.data;
    },
    onSuccess: () => {
      setProblema(null);
      setEnviado(correo.trim().toLowerCase());
      setAbierto(false);
      setCorreo('');
      setNombre('');
      void cache.invalidateQueries({ queryKey: ['admin', 'club', clubId] });
    },
    onError: (e) => setProblema(aProblema(e)),
  });

  const cancelar = () => {
    setAbierto(false);
    setProblema(null);
  };

  if (!abierto) {
    return (
      <div className="tenue">
        {enviado && (
          <span role="status">
            Invitación de dueño enviada a {enviado}. Vale 14 días.{' '}
          </span>
        )}
        <button
          type="button"
          className="btn-plano"
          onClick={() => {
            setEnviado(null);
            setAbierto(true);
          }}
        >
          Cambiar dueño
        </button>
      </div>
    );
  }

  return (
    <form
      className="formulario"
      onSubmit={(e) => {
        e.preventDefault();
        cambiar.mutate();
      }}
    >
      <h3>Cambiar el dueño del club</h3>
      <p>
        Le llega una invitación de dueño al correo nuevo.{' '}
        <strong>{actual}</strong> pierde el acceso al club; si además es de
        Kiap, sigue entrando al back office.
      </p>
      <p className="tenue">
        No uses un correo con el que se entra al back office: esa cuenta abre
        siempre el back office, y el dueño no vería su club.
      </p>
      <label>
        Correo del dueño
        <input
          type="email"
          required
          autoComplete="off"
          value={correo}
          onChange={(e) => setCorreo(e.target.value)}
        />
      </label>
      <label>
        Nombre (opcional)
        <input
          type="text"
          maxLength={80}
          value={nombre}
          onChange={(e) => setNombre(e.target.value)}
        />
      </label>

      {problema && <PanelError problema={problema} />}

      <div className="acciones">
        <button
          type="submit"
          className="btn-peligro"
          disabled={cambiar.isPending || correo.trim() === ''}
        >
          {cambiar.isPending ? 'Enviando…' : 'Cambiar y enviar invitación'}
        </button>
        <button type="button" className="btn-plano" onClick={cancelar}>
          Cancelar
        </button>
      </div>
    </form>
  );
}
